import { TFunction } from '@lyearn/i18n';
import { UserEntityConfig } from '@/components/FilterManager/EntityFilter/entity/user';
import {
  FilterBodyListConfig,
  FilterBodyUIConfig,
} from '@/components/FilterNew/components/Body/types';
import {
  useFilterDummyOptionsQuery,
  useFilterDummyValue,
} from '@/components/FilterNew/hooks/useDummies';
import { FilterBodyConfig, NewFilterOption } from '@/components/FilterNew/types';
import DateTagFilter from '@/components/Filters/DateFilter';
import { valueFieldName } from '@/components/Filters/NestedFilter/NestedFilterPill/tagFilters/useTagStateAndMethods';
import NumberFilter from '@/components/Filters/NumberFilter';
import TextFilter from '@/components/Filters/TextFilter';
import { NewTagValues } from '@/modules/corporate/modules/tags/types';
import getTagIcon from '@/modules/corporate/modules/tags/views/TagsManager/utils/getTagIcon';
import { EntityTagInput, FilterOperatorEnum, TagTypeEnum } from '@/types/schema';

import { isTagValueEmptyNew } from '../../SortAndFilter/components/TagsFilterContainer/utils';
import {
  DateTagFilterTypeFragment,
  TagFilterTypeFragment,
} from './graphql/queries/generated/tagFilter';
import { useTagFilterValue } from './hooks/useTagFilterValue';
import { useTagOptionsQuery } from './hooks/useTagOptionsQuery';
import { useTagsOptionsQuery } from './hooks/useTagsOptionsQuery';
import { TagsQueryParams, TagTypeFilterOption } from './types';

const getValue = (selectedTagItems: NewFilterOption<{}>[], type: TagTypeEnum) => {
  let value: NonNullable<NewTagValues>;
  switch (type) {
    case TagTypeEnum.Text:
      value = selectedTagItems[0].value;
      break;
    case TagTypeEnum.Number:
      value = Number(selectedTagItems[0].value);
      break;
    case TagTypeEnum.Date:
      value = JSON.parse(selectedTagItems[0].value);
      break;
    default:
      value = selectedTagItems.map((item) => item.value);
      break;
  }
  return value;
};

export const adaptSelectedTagsFilterItemsToState: (
  tag: TagFilterTypeFragment,
) => FilterBodyListConfig['adaptSelectedFilterItemsToState'] =
  (tag: TagFilterTypeFragment) => (selectedTagItems, id, filterState) => {
    const tagFilters = ((filterState.tagFilters ?? []) as EntityTagInput[]).filter(
      (tag) => tag.tagId !== id,
    );
    const value = getValue(selectedTagItems, tag.type);
    if (isTagValueEmptyNew(value, tag)) {
      return filterState;
    }

    return {
      ...filterState,
      tagFilters: [
        ...tagFilters,
        {
          tagId: id,
          type: tag.type,
          [valueFieldName[tag.type]]: value,
        },
      ],
    };
  };

const getCustomRenderer = (type: TagTypeEnum) => {
  switch (type) {
    case TagTypeEnum.Number:
      return NumberFilter;
    case TagTypeEnum.Text:
      return TextFilter;
    case TagTypeEnum.Date:
      return DateTagFilter;
    default:
      return undefined;
  }
};

const getTagFilterListConfig = (
  tag: TagFilterTypeFragment,
): {
  listConfig: FilterBodyConfig<any>['listConfig'];
  queryParams?: FilterBodyConfig<undefined | { tagId: string }>['queryParams'];
} => {
  switch (tag.type) {
    case TagTypeEnum.Number:
    case TagTypeEnum.Text:
    case TagTypeEnum.Date:
      return {
        listConfig: {
          listVariant: 'multi-grouped-finite',
          useOptionsQuery: useFilterDummyOptionsQuery,
          useFilterValues: useFilterDummyValue,
          adaptSelectedFilterItemsToState: adaptSelectedTagsFilterItemsToState(tag),
        },
      };
    case TagTypeEnum.User:
      return {
        listConfig: {
          ...UserEntityConfig,
          adaptSelectedFilterItemsToState: adaptSelectedTagsFilterItemsToState(tag),
        },
      };
    default:
      return {
        listConfig: {
          listVariant: 'multi-ungrouped-infinite',
          useOptionsQuery: useTagOptionsQuery,
          useFilterValues: useTagFilterValue,
          adaptSelectedFilterItemsToState: adaptSelectedTagsFilterItemsToState(tag),
        },
        queryParams: {
          tagId: tag.id,
        },
      };
  }
};

const getTagUIConfig = (tag: TagFilterTypeFragment): FilterBodyUIConfig | undefined => {
  switch (tag.type) {
    case TagTypeEnum.Date: {
      const dateTag = tag as DateTagFilterTypeFragment;
      return {
        datePicker: { includeEndDate: dateTag.includeEndDate, includeTime: dateTag.includeTime },
      };
    }
    default:
      return undefined;
  }
};

export const getTagFilterOptionWithConfig = (
  t: TFunction,
  tag: TagFilterTypeFragment,
): TagTypeFilterOption<any> => {
  const option: TagTypeFilterOption<{ tagId: string }> = {
    value: tag.id,
    label: tag.name,
    Icon: () => getTagIcon(tag)!,
    configToRenderFilterBody: {
      id: tag.id,
      headerProps: { label: tag.name },
      initialSelectedOperator: {
        id: FilterOperatorEnum.Is,
        label: t('Common:Filter.Is'),
        value: FilterOperatorEnum.Is,
      },
      CustomRenderer: getCustomRenderer(tag.type),
      listConfig: getTagFilterListConfig(tag).listConfig,
      queryParams: getTagFilterListConfig(tag).queryParams,
      uiConfig: getTagUIConfig(tag),
    },
  };
  return option;
};

export const TagsEntityConfig: FilterBodyListConfig<TagsQueryParams> = {
  useOptionsQuery: useTagsOptionsQuery,
  listVariant: 'single-ungrouped-infinite',
  useFilterValues: useFilterDummyValue,
};
